/**
 * Decision Rights Store
 *
 * Manages cycle-level decision rights — who may decide what, using which
 * execution modes, under what delegation rules, and who may reassign each right.
 *
 * Build Contract §2.3: DB is the system of record. Nothing here persists to
 * localStorage. All mutations go through transactional RPCs (§2.3a).
 *
 * Schema:  supabase/migrations/217_decision_rights.sql
 * Types:   src/types/prioritization/decisionRights.ts
 *
 * RPCs
 *   assign_decision_right             — upsert a right, validate constraints, governance event
 *   delegate_decision_right           — create delegation record (optional end date, 980)
 *   revoke_delegation                 — mark delegation revoked
 *   extend_decision_right_delegation  — move a delegation's end date, with a basis (980)
 *
 * Read path: direct table select (no RPC needed — reads are not authority transitions)
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import {
  type DecisionRight,
  type DelegatedDecisionRight,
  type DecisionRightsConfiguration,
  type DecisionExecutionMode,
  type OwnerBindingType,
  type DelegationConstraints,
  createDefaultDecisionRights,
} from '../../types/prioritization/decisionRights';
import type { CanonicalSessionType, CanonicalDecisionClassKind } from '../../types/prioritization/session';

// ============================================================================
// DB → TS converters
// ============================================================================

function dbRowToDecisionRight(row: Record<string, unknown>): DecisionRight {
  return {
    id: row.id as string,
    domain: row.domain as CanonicalSessionType,
    decisionClass: row.decision_class as CanonicalDecisionClassKind,
    scopeSelector: row.scope_selector as string,
    owner: {
      bindingType: row.owner_binding_type as OwnerBindingType,
      roleId: (row.owner_role_id as string | null) ?? undefined,
      principalId: (row.owner_principal_id as string | null) ?? undefined,
    },
    allowedExecutionModes: (row.allowed_execution_modes as string[]) as DecisionExecutionMode[],
    delegationConstraints: (row.delegation_constraints as DelegationConstraints | null) ?? undefined,
    metaAssignment: {
      bindingType: row.meta_binding_type as OwnerBindingType,
      roleId: (row.meta_role_id as string | null) ?? undefined,
      principalId: (row.meta_principal_id as string | null) ?? undefined,
    },
    assignedAt: row.assigned_at ? new Date(row.assigned_at as string) : undefined,
    assignedBy: (row.assigned_by as string | null) ?? undefined,
  };
}

function dbRowToDelegatedRight(row: Record<string, unknown>): DelegatedDecisionRight {
  return {
    id: row.id as string,
    sourceRightId: row.source_right_id as string,
    delegatePrincipalId: row.delegate_principal_id as string,
    allowedExecutionModes: (row.allowed_execution_modes as string[]) as DecisionExecutionMode[],
    depth: row.depth as number,
    accepted: row.accepted as boolean,
    acceptedAt: row.accepted_at ? new Date(row.accepted_at as string) : undefined,
    delegatedAt: new Date(row.delegated_at as string),
    delegatedBy: (row.delegated_by as string | null) ?? undefined,
    expiresAt: row.expires_at ? new Date(row.expires_at as string) : undefined,
    revoked: row.revoked as boolean,
    revokedAt: row.revoked_at ? new Date(row.revoked_at as string) : undefined,
    revokedBy: (row.revoked_by as string | null) ?? undefined,
  };
}

// ============================================================================
// Param types for mutations
// ============================================================================

export interface AssignRightParams {
  cycleId: string;
  domain: CanonicalSessionType;
  decisionClass: CanonicalDecisionClassKind;
  scopeSelector?: string;
  ownerBindingType: OwnerBindingType;
  ownerRoleId?: string;
  ownerPrincipalId?: string;
  allowedModes: DecisionExecutionMode[];
  delegationConstraints?: DelegationConstraints;
  metaBindingType?: OwnerBindingType;
  metaRoleId?: string;
  metaPrincipalId?: string;
  callerId: string;
}

export interface DelegateRightParams {
  rightId: string;
  delegatePrincipalId: string;
  allowedModes?: DecisionExecutionMode[];
  expiresAt?: Date;
  callerId: string;
}

export interface ExtendDelegationParams {
  delegationId: string;
  /** The new end; null = stands until revoked. Must be in the future. */
  expiresAt: Date | null;
  /** Why the end date changes. Required. */
  basis: string;
}

// ============================================================================
// Store interface
// ============================================================================

interface DecisionRightsState {
  /** Loaded rights keyed by cycle ID */
  rightsByCycle: Record<string, DecisionRight[]>;
  /** Loaded delegations keyed by cycle ID */
  delegationsByCycle: Record<string, DelegatedDecisionRight[]>;
  /** Whether a load is in flight for each cycle */
  loading: Record<string, boolean>;
  /** Last load error per cycle */
  error: Record<string, string | null>;

  // ── Read ──────────────────────────────────────────────────────────────────

  /**
   * Load decision rights + delegations for a cycle from the DB.
   * No-ops if already loaded and force is false.
   */
  loadRights: (cycleId: string, force?: boolean) => Promise<void>;

  /**
   * Returns the full DecisionRightsConfiguration for a cycle, or null if not
   * yet loaded.
   *
   * When the DB has no rows for the cycle, returns null — the panel falls back
   * to createDefaultDecisionRights() and shows "defaults not yet configured."
   * After the user clicks Configure and calls assignRight(), the panel uses real
   * DB-backed data and survives refresh (§2.4).
   */
  getConfigForCycle: (cycleId: string) => DecisionRightsConfiguration | null;

  getRightsForCycle: (cycleId: string) => DecisionRight[];
  getDelegationsForCycle: (cycleId: string) => DelegatedDecisionRight[];
  isLoading: (cycleId: string) => boolean;
  getError: (cycleId: string) => string | null;

  // ── Mutations (§2.3a — via RPC) ───────────────────────────────────────────

  /** Upsert a decision right. Calls assign_decision_right RPC. */
  assignRight: (params: AssignRightParams) => Promise<{
    success: boolean;
    error?: string;
    right?: DecisionRight;
  }>;

  /** Create a delegation. Calls delegate_decision_right RPC. */
  delegateRight: (params: DelegateRightParams) => Promise<{
    success: boolean;
    error?: string;
    delegation?: DelegatedDecisionRight;
  }>;

  /** Revoke a delegation. Calls revoke_delegation RPC. */
  revokeDelegation: (delegationId: string, callerId: string) => Promise<{
    success: boolean;
    error?: string;
  }>;

  /**
   * Change when a delegation ends — later, earlier, or not at all. Calls
   * extend_decision_right_delegation (980); the database judges who may.
   */
  extendDelegation: (params: ExtendDelegationParams) => Promise<{
    success: boolean;
    error?: string;
    expiresAt?: Date | null;
  }>;

  /**
   * Initialize a cycle's decision rights from the defaults factory and persist
   * them all to DB. Used by the "Configure" action when no rights exist yet.
   */
  initializeDefaults: (
    cycleId: string,
    cycleOwnerId: string,
    callerId: string
  ) => Promise<{ success: boolean; error?: string }>;
}

// ============================================================================
// Store implementation
// ============================================================================

export const useDecisionRightsStore = create<DecisionRightsState>((set, get) => ({
  rightsByCycle: {},
  delegationsByCycle: {},
  loading: {},
  error: {},

  // ── Selectors ─────────────────────────────────────────────────────────────

  getConfigForCycle: (cycleId) => {
    const rights = get().rightsByCycle[cycleId];
    if (!rights) return null;
    const delegations = get().delegationsByCycle[cycleId] ?? [];
    return {
      cycleId,
      rights,
      delegations,
      version: 1,
      configuredAt: rights[0]?.assignedAt ?? new Date(),
      configuredBy: rights[0]?.assignedBy ?? '',
    };
  },

  getRightsForCycle: (cycleId) => get().rightsByCycle[cycleId] ?? [],
  getDelegationsForCycle: (cycleId) => get().delegationsByCycle[cycleId] ?? [],
  isLoading: (cycleId) => get().loading[cycleId] ?? false,
  getError: (cycleId) => get().error[cycleId] ?? null,

  // ── Load ──────────────────────────────────────────────────────────────────

  loadRights: async (cycleId, force = false) => {
    const state = get();

    // Already loaded and not forcing
    if (!force && state.rightsByCycle[cycleId] !== undefined) return;
    // Already in flight
    if (state.loading[cycleId]) return;

    set(s => ({ loading: { ...s.loading, [cycleId]: true }, error: { ...s.error, [cycleId]: null } }));

    if (!isSupabaseAvailable()) {
      set(s => ({
        loading: { ...s.loading, [cycleId]: false },
        error: { ...s.error, [cycleId]: 'Database unavailable' },
      }));
      return;
    }

    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set(s => ({
        loading: { ...s.loading, [cycleId]: false },
        error: { ...s.error, [cycleId]: 'No Supabase client' },
      }));
      return;
    }

    try {
      // Fetch rights
      const { data: rightsData, error: rightsError } = await supabase
        .from('decision_rights')
        .select('*')
        .eq('cycle_id', cycleId)
        .order('domain')
        .order('decision_class');

      if (rightsError) {
        set(s => ({
          loading: { ...s.loading, [cycleId]: false },
          error: { ...s.error, [cycleId]: rightsError.message },
        }));
        return;
      }

      const rights = (rightsData ?? []).map(r => dbRowToDecisionRight(r as Record<string, unknown>));
      const rightIds = rights.map(r => r.id);

      // Fetch delegations for those rights (only if any rights exist)
      let delegations: DelegatedDecisionRight[] = [];
      if (rightIds.length > 0) {
        const { data: delData, error: delError } = await supabase
          .from('delegated_decision_rights')
          .select('*')
          .in('source_right_id', rightIds)
          .eq('revoked', false);

        if (!delError && delData) {
          delegations = delData.map(d => dbRowToDelegatedRight(d as Record<string, unknown>));
        }
      }

      set(s => ({
        rightsByCycle: { ...s.rightsByCycle, [cycleId]: rights },
        delegationsByCycle: { ...s.delegationsByCycle, [cycleId]: delegations },
        loading: { ...s.loading, [cycleId]: false },
        error: { ...s.error, [cycleId]: null },
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error loading decision rights';
      set(s => ({
        loading: { ...s.loading, [cycleId]: false },
        error: { ...s.error, [cycleId]: message },
      }));
    }
  },

  // ── assignRight ───────────────────────────────────────────────────────────

  assignRight: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('assign_decision_right', {
      p_cycle_id:               params.cycleId,
      p_domain:                 params.domain,
      p_decision_class:         params.decisionClass,
      p_scope_selector:         params.scopeSelector ?? '*',
      p_owner_binding_type:     params.ownerBindingType,
      p_owner_role_id:          params.ownerRoleId ?? null,
      p_owner_principal_id:     params.ownerPrincipalId ?? null,
      p_allowed_modes:          params.allowedModes,
      p_delegation_constraints: params.delegationConstraints ?? null,
      p_meta_binding_type:      params.metaBindingType ?? 'PRINCIPAL_IN_SCOPE',
      p_meta_role_id:           params.metaRoleId ?? null,
      p_meta_principal_id:      params.metaPrincipalId ?? null,
      p_caller_id:              params.callerId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as { success: boolean; error?: string; right?: Record<string, unknown> };
    if (!result.success) return { success: false, error: result.error };

    const right = result.right ? dbRowToDecisionRight(result.right) : undefined;

    // Update local state — refresh the full rights list for accuracy
    await get().loadRights(params.cycleId, true);

    return { success: true, right };
  },

  // ── delegateRight ─────────────────────────────────────────────────────────

  delegateRight: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('delegate_decision_right', {
      p_right_id:               params.rightId,
      p_delegate_principal_id:  params.delegatePrincipalId,
      p_allowed_modes:          params.allowedModes ?? null,
      p_expires_at:             params.expiresAt?.toISOString() ?? null,
      p_caller_id:              params.callerId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as { success: boolean; error?: string; delegation?: Record<string, unknown> };
    if (!result.success) return { success: false, error: result.error };

    const delegation = result.delegation ? dbRowToDelegatedRight(result.delegation) : undefined;

    // Find cycle for the right and reload
    const right = Object.values(get().rightsByCycle)
      .flat()
      .find(r => r.id === params.rightId);
    if (right) {
      // Append optimistically so the UI responds immediately; next load corrects
      set(s => {
        const cycleId = Object.keys(s.rightsByCycle).find(cid =>
          s.rightsByCycle[cid].some(r => r.id === params.rightId)
        );
        if (!cycleId || !delegation) return s;
        return {
          delegationsByCycle: {
            ...s.delegationsByCycle,
            [cycleId]: [...(s.delegationsByCycle[cycleId] ?? []), delegation],
          },
        };
      });
    }

    return { success: true, delegation };
  },

  // ── revokeDelegation ──────────────────────────────────────────────────────

  revokeDelegation: async (delegationId, callerId) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('revoke_delegation', {
      p_delegation_id: delegationId,
      p_caller_id:     callerId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as { success: boolean; error?: string };
    if (!result.success) return { success: false, error: result.error };

    // Remove from local state immediately
    set(s => {
      const updated: Record<string, DelegatedDecisionRight[]> = {};
      for (const [cycleId, dels] of Object.entries(s.delegationsByCycle)) {
        updated[cycleId] = dels.filter(d => d.id !== delegationId);
      }
      return { delegationsByCycle: updated };
    });

    return { success: true };
  },

  // ── extendDelegation ──────────────────────────────────────────────────────

  extendDelegation: async ({ delegationId, expiresAt, basis }) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };

    const { data, error } = await getTypedSupabaseClient().rpc('extend_decision_right_delegation', {
      p_id: delegationId,
      // NULL removes the end date; the generated Args type cannot say "nullable".
      p_expires_at: (expiresAt ? expiresAt.toISOString() : null) as string,
      p_basis: basis,
    });

    if (error) return { success: false, error: error.message };

    const result = data as { success: boolean; error?: string; expires_at?: string | null } | null;
    if (!result?.success) return { success: false, error: result?.error ?? 'The end date could not be changed.' };

    const next = result.expires_at ? new Date(result.expires_at) : null;
    set(s => {
      const updated: Record<string, DelegatedDecisionRight[]> = {};
      for (const [cycleId, dels] of Object.entries(s.delegationsByCycle)) {
        updated[cycleId] = dels.map(d => (d.id === delegationId ? { ...d, expiresAt: next ?? undefined } : d));
      }
      return { delegationsByCycle: updated };
    });

    return { success: true, expiresAt: next };
  },

  // ── initializeDefaults ────────────────────────────────────────────────────

  initializeDefaults: async (cycleId, cycleOwnerId, callerId) => {
    const defaults = createDefaultDecisionRights(cycleId, cycleOwnerId, callerId);

    const results = await Promise.all(
      defaults.rights.map(right =>
        get().assignRight({
          cycleId,
          domain: right.domain,
          decisionClass: right.decisionClass,
          scopeSelector: right.scopeSelector,
          ownerBindingType: right.owner.bindingType,
          ownerRoleId: right.owner.roleId,
          ownerPrincipalId: right.owner.principalId,
          allowedModes: right.allowedExecutionModes,
          delegationConstraints: right.delegationConstraints,
          metaBindingType: right.metaAssignment.bindingType,
          metaRoleId: right.metaAssignment.roleId,
          metaPrincipalId: right.metaAssignment.principalId,
          callerId,
        })
      )
    );

    const failed = results.filter(r => !r.success);
    if (failed.length > 0) {
      return {
        success: false,
        error: `${failed.length} of ${results.length} rights failed to persist: ${failed[0].error}`,
      };
    }

    return { success: true };
  },
}));
